import { useState, useEffect, useCallback } from 'react';

/**
 * Tailwind breakpoints exported as TypeScript constants.
 * These match the values in tailwind.config.js for consistency.
 */
export const BREAKPOINTS = {
  xs: 375,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Determines the current breakpoint based on window width.
 */
function getBreakpoint(width: number): BreakpointKey {
  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
}

/**
 * Hook that provides responsive breakpoint information.
 *
 * Uses matchMedia for better performance compared to resize listeners.
 * Provides boolean helpers for common responsive patterns.
 *
 * @example
 * ```tsx
 * const { isMobile, isTablet, isDesktop, breakpoint } = useBreakpoint();
 *
 * if (isMobile) {
 *   return <MobileLayout />;
 * }
 * ```
 */
export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState<BreakpointKey>(() => {
    if (typeof window === 'undefined') return 'xs';
    return getBreakpoint(window.innerWidth);
  });

  const updateBreakpoint = useCallback(() => {
    setBreakpoint(getBreakpoint(window.innerWidth));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Use matchMedia for each breakpoint for better performance
    const mediaQueries: { query: MediaQueryList; handler: () => void }[] = [];

    // Create media query listeners for each breakpoint
    Object.entries(BREAKPOINTS).forEach(([, value]) => {
      const query = window.matchMedia(`(min-width: ${value}px)`);

      const handler = () => updateBreakpoint();

      // Modern browsers
      if (query.addEventListener) {
        query.addEventListener('change', handler);
      } else {
        // Legacy browsers (Safari < 14)
        query.addListener(handler);
      }

      mediaQueries.push({ query, handler });
    });

    // Initial check
    updateBreakpoint();

    // Cleanup
    return () => {
      mediaQueries.forEach(({ query, handler }) => {
        if (query.removeEventListener) {
          query.removeEventListener('change', handler);
        } else {
          query.removeListener(handler);
        }
      });
    };
  }, [updateBreakpoint]);

  // Derived boolean values for common patterns
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm';
  const isTablet = breakpoint === 'md';
  const isDesktop = breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === '2xl';

  // More specific checks
  const isSmallMobile = breakpoint === 'xs';
  const isLargeMobile = breakpoint === 'sm';
  const isSmallDesktop = breakpoint === 'lg';
  const isLargeDesktop = breakpoint === 'xl' || breakpoint === '2xl';

  return {
    breakpoint,
    isMobile,
    isTablet,
    isDesktop,
    isSmallMobile,
    isLargeMobile,
    isSmallDesktop,
    isLargeDesktop,
  };
}

/**
 * Hook that checks if viewport is at or above a specific breakpoint.
 *
 * @example
 * ```tsx
 * const isAboveMd = useMinBreakpoint('md');
 * // true if viewport >= 768px
 * ```
 */
export function useMinBreakpoint(minBreakpoint: BreakpointKey): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= BREAKPOINTS[minBreakpoint];
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const query = window.matchMedia(`(min-width: ${BREAKPOINTS[minBreakpoint]}px)`);

    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      setMatches(e.matches);
    };

    // Initial check
    setMatches(query.matches);

    // Modern browsers
    if (query.addEventListener) {
      query.addEventListener('change', handler as (e: MediaQueryListEvent) => void);
    } else {
      query.addListener(handler as (this: MediaQueryList, ev: MediaQueryListEvent) => void);
    }

    return () => {
      if (query.removeEventListener) {
        query.removeEventListener('change', handler as (e: MediaQueryListEvent) => void);
      } else {
        query.removeListener(handler as (this: MediaQueryList, ev: MediaQueryListEvent) => void);
      }
    };
  }, [minBreakpoint]);

  return matches;
}

/**
 * Hook that checks if viewport is below a specific breakpoint.
 *
 * @example
 * ```tsx
 * const isBelowLg = useMaxBreakpoint('lg');
 * // true if viewport < 1024px
 * ```
 */
export function useMaxBreakpoint(maxBreakpoint: BreakpointKey): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth < BREAKPOINTS[maxBreakpoint];
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const query = window.matchMedia(`(max-width: ${BREAKPOINTS[maxBreakpoint] - 1}px)`);

    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      setMatches(e.matches);
    };

    // Initial check
    setMatches(query.matches);

    // Modern browsers
    if (query.addEventListener) {
      query.addEventListener('change', handler as (e: MediaQueryListEvent) => void);
    } else {
      query.addListener(handler as (this: MediaQueryList, ev: MediaQueryListEvent) => void);
    }

    return () => {
      if (query.removeEventListener) {
        query.removeEventListener('change', handler as (e: MediaQueryListEvent) => void);
      } else {
        query.removeListener(handler as (this: MediaQueryList, ev: MediaQueryListEvent) => void);
      }
    };
  }, [maxBreakpoint]);

  return matches;
}
